import _ from 'lodash';
import { Loader } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import React, {
    useCallback,
    useState,
    useContext
} from 'react';
import { AgencyAdminService } from 'twia-capability-agencyadmin';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { Button } from '@jutro/legacy/components';
import { Link } from '@jutro/router';
import messages from './EditPrimaryContact.messages';
import metadata from './EditPrimaryContact.metadata.json5';
import styles from './EditPrimaryContact.module.scss';
import AddUser from "../AddUser/AddUser";
import DeactivateAgency from "../DeactivateAgency/DeactivateAgency";
import DeactivateUser from "../DeactivateUser/DeactivateUser";
import EditUser from "../EditUser/EditUser";

function EditPrimaryContact(props) {
    const { userVM, group, updatePrimaryContact, groupPublicID, usersData, associationName_TWIA } = props;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [contacts, setContacts] = useState(group.users.value);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const [agencyList, setAgencyList] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [showUserInfo, setShowUserInfo] = useState(false);
    const [selectedUser, setSelectedUser] = useState({});
    const [showEdit, setShowEdit] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showDeactivateAgency, setDeactivateAgency] = useState(false);
    const [showActivateUser, setShowActivateUser] = useState(false);
    const [showDeactivateUser, setShowDeactivateUser] = useState(false);
    const [showUsersList, setShowUsersList] = useState(true);
    const [selectedUserVM, setSelectedUserVM] = useState(userVM);
    const viewModelService = useContext(ViewModelServiceContext);

    const isAgencyOrgAdmin = group?.users?.value.some(obj => obj.isOrgAdmin);

    const checkIfAlreadySelected = useCallback((item) => {
        try {
            const agencyContact = group?.primaryPersonContact || group?.primaryCompanyContact;
            const organizationContact = userVM?.organization_EXT?.primaryPersonContact || userVM?.organization_EXT?.primaryCompanyContact;
            const currContact = !_.isUndefined(organizationContact?.value) ? organizationContact : agencyContact;

            const workNumber = currContact?.workNumber?.value;
            const detailMismatch = currContact?.firstName?.value !== item.firstName ||
                currContact?.lastName?.value !== item.lastName ||
                currContact?.emailAddress1?.value !== item.email ||
                workNumber === null ||
                workNumber.replace(/-/g, '') !== item.phoneNumber.replace(/-/g, '');

            return detailMismatch && item.status;
        } catch (e) {
            // if no data is set the group, it will fail. may happen with migrated data
            return true;
        }
    }, [group, userVM]);

    const cancelDeactivateAgency = useCallback((value) => {
        if (value) {
            setDeactivateAgency(false);
            setShowUsersList(true);
        }
    }, []);

    const getPrimaryContactSelected = useCallback((item) => (
        checkIfAlreadySelected(item) ?
            <Button onClick={() => updatePrimaryContact(item, group)}>
                {translator(messages.select)}
            </Button> : null
    ), [checkIfAlreadySelected, group, translator, updatePrimaryContact]);

    const getStatus = (item) => item.status ? 'Active' : 'Suspended';
    const getEftStatus = (item) => item.isEFTEnabled ? 'Yes' : 'No';

    const openUserInfo = (e, item) => {
        const groupList = [];

        if (associationName_TWIA === 'twia') {
            const userGroupPromise = AgencyAdminService.getUserGroupsList(item.publicID, authHeader).then((res) => {
                for (const key in res) {
                    const list = {};

                    list.agencyName = key;
                    list.isAdmin = res[key];
                    groupList.push(list);
                }

                setAgencyList(groupList);
            })

            Promise.all([userGroupPromise]).then(() => {
                setIsLoadingData(false);
            })
        } else {
            const userGroupPromise = AgencyAdminService.getUserGroupsLis_tfpa(item.publicID, authHeader).then((res) => {
                for (const key in res) {
                    const list = {};

                    list.agencyName = key;
                    list.isAdmin = res[key];
                    groupList.push(list);
                }

                setAgencyList(groupList);
            })

            Promise.all([userGroupPromise]).then(() => {
                setIsLoadingData(false);
            })
        }

        setSelectedUser(item);

        const user = viewModelService?.create(
            item,
            'pc',
            'org.twia.elevate.edge.capabilities.agent.dto.UserDTO'
        )

        setSelectedUserVM(user);
        setShowUserInfo(true);
    };

    // DE18080
    const getUsernameLink = (item) => (
        <div style={{ textAlign: "left" }}>
          <Link onClick={(e) => openUserInfo(e, item)}>
            {item.userName_EXT}
          </Link>
        </div>
      );
       

    const clickEdit = () => {
        setShowEdit(true);
    };

    const clickDeactivateUser = useCallback(() => {
        setShowDeactivateUser(true);
        setShowActivateUser(false);
        setShowUsersList(false);
    }, []);

    const clickActivateUser = useCallback(() => {
        setShowDeactivateUser(false);
        setShowActivateUser(true);
        setShowUsersList(false);
    }, []);

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
        },
        userListTable: {
            data: contacts
        },
        eftEnableClumn: {
            // DE15307
            visible: (_.get(userVM, 'associationName_TWIA.value.code') === 'twia') && ((group?.isAgencyAdmin?.value !== true) && (userVM?.isOrgAdmin?.value !== true))
        },
        userTable: {
            visible: showAddUser ? showUsersList : !showUserInfo || showDeactivateAgency ? showUsersList : !showUserInfo
        },
        activateUser: {
            visible: false
        },
        deactivateUserElement: {
            visible: showDeactivateUser || showActivateUser,
            userData: userVM,
            userActionFlag: showDeactivateUser === true ? 'deactivate' : 'activate'
        },
        userInformation: {
            visible: showUserInfo
        },
        deactivateButton: {
            visible: !_.isUndefined(userVM) && !_.isUndefined(userVM.status) && userVM.status.value
        },
        activateButton: {
            visible: !(!_.isUndefined(userVM) && !_.isUndefined(userVM.status) && userVM.status.value && userVM.isLocked_EXT && !userVM.isLocked_EXT.value)
        },
        userInfoContainer: {
            visible: !showEdit
        },
        userRolesTableGridDiv: {
            visible: !showEdit
        },
        userRolesTableHeader: {
            visible: !showEdit
        },
        userRolesTableHeaderSpacing: {
            visible: !showEdit
        },
        userRolesTable: {
            data: selectedUser?.userRoles?.filter(role => role.isApplicable) // DE15372-To show only assigned roles in view mode
        },
        agencyInformationTable: {
            visible: !showEdit
        },
        agencyListTable: {
            data: agencyList
        },
        addUser: {
            groupPublicID,
            visible: showAddUser,
            usersData,
            isMasterGroup: group.isMasterGroup.value,// DE15326
            associationName_TWIA // Needed for add user
        },
        editUser: {
            visible: showEdit,
            agencyList,
            userListVM: selectedUserVM,
            onCancel: () => setShowEdit(false),
            showEdit
        },
        activateAgencyButton: {
            visible: group.canDeactivate.value && isAgencyOrgAdmin
        },
        deactivateAgencyElement: {
            groupPublicID,
            visible: showDeactivateAgency,
            cancelDeactivateAgency
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            addUserComponent: AddUser,
            editUserComponent: EditUser,
            deactivateAgencyComponent: DeactivateAgency,
            deactivateUserComponent: DeactivateUser
        },
        resolveCallbackMap: {
            getUsernameLink,
            getPrimaryContactSelected,
            getStatus,
            getEftStatus,
            clickEdit,
            clickDeactivateUser,
            clickActivateUser
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={selectedUserVM}
            overrideProps={overrideProps}
            // onValueChange={handleSearchValueChange}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

EditPrimaryContact.propTypes = {

};

EditPrimaryContact.defaultProps = {

};

export default EditPrimaryContact;
